import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { BackOfficeAuthService } from '../services/back-office-auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Interceptor para rutas del back office.
 * - Agrega Authorization: Bearer <token> a peticiones /api/users/ y /admin/
 * - Si recibe 401, intenta refresh automático y reintenta la petición
 * - Si hay múltiples 401 simultáneos, solo hace un refresh y encola los demás
 */
export const backOfficeAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(BackOfficeAuthService);

  const isBackOfficeRequest = req.url.includes('/api/users/') || req.url.includes('/admin/');
  if (!isBackOfficeRequest) {
    return next(req);
  }

  // No interceptar endpoints públicos
  const publicPaths = [
    '/auth/login/',
    '/auth/refresh/',
    '/auth/force-logout/',
    '/auth/verify-identity/',
    '/auth/forgot-password/',
    '/auth/2fa/verify/',
  ];
  if (publicPaths.some(p => req.url.includes(p))) {
    return next(req);
  }

  const token = authService.getAccessToken();
  const authReq = token
    ? req.clone({ setHeaders: { 'Authorization': `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && authService.getRefreshToken()) {
        return handle401(authService, req, next);
      }
      return throwError(() => error);
    })
  );
};

function handle401(
  authService: BackOfficeAuthService,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap(() => {
        isRefreshing = false;
        const newToken = authService.getAccessToken()!;
        refreshTokenSubject.next(newToken);
        return next(req.clone({
          setHeaders: { 'Authorization': `Bearer ${newToken}` },
        }));
      }),
      catchError((err) => {
        isRefreshing = false;
        authService.clearSession();
        authService.navigateToLogin();
        return throwError(() => err);
      })
    );
  }

  // Si ya se está refrescando, esperar a que termine y reintentar
  return refreshTokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap(token => {
      return next(req.clone({
        setHeaders: { 'Authorization': `Bearer ${token}` },
      }));
    })
  );
}
